import { Controller } from "@hotwired/stimulus"

/**
 * Item Variants Controller
 * Manages dynamic variant rows (add/remove/reorder) for item stock management.
 */
export default class extends Controller {
  static targets = ["list", "template", "totalStock", "stockInput", "variantRow", "positionInput"]

  connect() {
    this.nextIndex = this.getMaxIndex() + 1
    this.updateTotal()
    this.initDragAndDrop()
  }

  disconnect() {
    this.cleanupDragAndDrop()
  }

  /**
   * Initialize native HTML5 drag & drop for variant rows
   */
  initDragAndDrop() {
    if (!this.hasListTarget) return

    this.listTarget.querySelectorAll(".variant-row").forEach(row => {
      this.setupRowDragEvents(row)
    })
  }

  setupRowDragEvents(row) {
    const handle = row.querySelector(".variant-handle")
    if (!handle) return

    // Make row draggable only via handle
    handle.addEventListener("mousedown", () => {
      row.setAttribute("draggable", "true")
    })

    handle.addEventListener("mouseup", () => {
      row.setAttribute("draggable", "false")
    })

    row.addEventListener("dragstart", this.handleDragStart.bind(this))
    row.addEventListener("dragend", this.handleDragEnd.bind(this))
    row.addEventListener("dragover", this.handleDragOver.bind(this))
    row.addEventListener("drop", this.handleDrop.bind(this))
  }

  cleanupDragAndDrop() {
    if (!this.hasListTarget) return

    this.listTarget.querySelectorAll(".variant-row").forEach(row => {
      row.removeEventListener("dragstart", this.handleDragStart.bind(this))
      row.removeEventListener("dragend", this.handleDragEnd.bind(this))
      row.removeEventListener("dragover", this.handleDragOver.bind(this))
      row.removeEventListener("drop", this.handleDrop.bind(this))
    })
  }

  handleDragStart(event) {
    this.draggedElement = event.currentTarget
    event.currentTarget.classList.add("opacity-50", "scale-95")
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", "variant")
  }

  handleDragEnd(event) {
    event.currentTarget.classList.remove("opacity-50", "scale-95")
    event.currentTarget.setAttribute("draggable", "false")
    this.listTarget.querySelectorAll(".variant-row").forEach(row => {
      row.classList.remove("border-indigo-500", "border-2")
    })
    this.draggedElement = null
  }

  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const target = event.currentTarget
    if (target !== this.draggedElement) {
      target.classList.add("border-indigo-500", "border-2")
    }
  }

  handleDrop(event) {
    event.preventDefault()
    const target = event.currentTarget
    target.classList.remove("border-indigo-500", "border-2")

    if (target === this.draggedElement || !this.draggedElement) return

    const allRows = [...this.listTarget.querySelectorAll(".variant-row")]
    const draggedIndex = allRows.indexOf(this.draggedElement)
    const targetIndex = allRows.indexOf(target)

    if (draggedIndex < targetIndex) {
      target.after(this.draggedElement)
    } else {
      target.before(this.draggedElement)
    }

    this.updatePositions()
  }

  /**
   * Update position values after reordering
   */
  updatePositions() {
    this.listTarget.querySelectorAll(".variant-row").forEach((row, index) => {
      const positionInput = row.querySelector('[data-item-variants-target="positionInput"]')
      if (positionInput) {
        positionInput.value = index
      }
    })
  }

  /**
   * Get the maximum index currently used in variant fields
   */
  getMaxIndex() {
    let maxIndex = -1
    const inputs = this.listTarget.querySelectorAll('input[name*="[item_variants_attributes]"]')

    inputs.forEach((input) => {
      const match = input.name.match(/\[item_variants_attributes\]\[(\d+)\]/)
      if (match) {
        const index = parseInt(match[1], 10)
        maxIndex = Math.max(maxIndex, index)
      }
    })

    return maxIndex
  }

  /**
   * Add a new variant row
   */
  addVariant(event) {
    event.preventDefault()

    const template = this.templateTarget.innerHTML
    const index = this.nextIndex++

    // Replace placeholder with actual index
    const html = template.replace(/NEW_VARIANT/g, index)

    // Create container and insert
    const container = document.createElement("div")
    container.innerHTML = html
    const row = container.firstElementChild

    this.listTarget.appendChild(row)

    // Setup drag events for new row
    this.setupRowDragEvents(row)

    // Focus the label input
    const labelInput = row.querySelector('input[name*="[label]"]')
    if (labelInput) {
      labelInput.focus()
    }

    this.updateTotal()
    this.updatePositions()
  }

  /**
   * Update the total stock display
   */
  updateTotal() {
    if (!this.hasTotalStockTarget) return

    let total = 0

    // Sum all visible (not destroyed) stock inputs
    this.stockInputTargets.forEach((input) => {
      const row = input.closest(".variant-row")
      if (!row) return

      // Check if row is marked for destruction
      const destroyField = row.querySelector('input[name*="[_destroy]"]')
      if (destroyField && destroyField.value === "true") return

      const value = parseInt(input.value, 10) || 0
      total += value
    })

    this.totalStockTarget.textContent = total
  }
}
